import { useAtom } from '@reatom/npm-react'
import { memo, useMemo } from 'react'
import { Link } from 'react-router'
import styled from 'styled-components'

import { Tooltip } from '@blade/ui-kit'
import { PremiumTicker } from '@root/entities/premium/ui/premium-ticker/premium-ticker'
import { TradeRoute } from '@root/features/routing'
import { FadeOverflow } from '@root/shared/ui-kit'

import type { PairModel } from '../../../atoms'
import type { CurrencyModel } from '@root/entities/currency/model'
import type { PremiumMetaModel } from '@root/entities/premium/atoms'
import type { ClassNameProps } from '@root/shared/react'

export type PairOrCurrencyProps =
  | {
      pair: PairModel
      currency?: undefined
      premiumInfo?: undefined
    }
  | {
      pair?: undefined
      currency: CurrencyModel
      premiumInfo?: PremiumMetaModel
    }

export type PairTickerProps = {
  asLink?: boolean
  withFade?: boolean
  onLinkClick?: () => void
} & ClassNameProps &
  PairOrCurrencyProps

export const PairTicker: React.FC<PairTickerProps> = memo((props) => {
  const { asLink = true, withFade = true, onLinkClick, className } = props

  const premiumInfo = props.pair ? props.pair.premiumInfo : props.premiumInfo
  const quote = props.pair ? props.pair.quote : props.currency

  const [premium] = useAtom((ctx) => {
    if (premiumInfo) {
      return ctx.spy(premiumInfo.premium)
    }
    return false
  })

  const link = useMemo(() => {
    if (props.pair) {
      return TradeRoute.build({
        pairAddress: props.pair.address,
        network: props.pair.network,
      })
    }
    return undefined
  }, [props.pair])

  const renderTicker = () => (
    <PremiumTicker premiumInfo={premiumInfo}>
      {quote.ticker || (
        <span style={{ width: 8, display: 'inline-block' }}>&nbsp;</span>
      )}
    </PremiumTicker>
  )

  const children = withFade ? (
    <FadeOverflow variant="horizontal" inlineStart={0} inlineEnd={10}>
      {({ horizontalOverflow }) =>
        horizontalOverflow ? (
          <Tooltip content={quote.ticker}>{renderTicker()}</Tooltip>
        ) : (
          renderTicker()
        )
      }
    </FadeOverflow>
  ) : (
    renderTicker()
  )

  if (asLink && link) {
    return (
      <Root
        as={Link}
        onClick={(e) => {
          e.stopPropagation()
          onLinkClick?.()
        }}
        to={link}
        data-premium={premium}
        className={className}
        $withFade={withFade}
      >
        {children}
      </Root>
    )
  }

  return (
    <Root data-premium={premium} className={className} $withFade={withFade}>
      {children}
    </Root>
  )
})

const Root = styled.div<{ $withFade?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  overflow: ${({ $withFade = true }) => ($withFade ? 'hidden' : 'visible')};

  &:link,
  &:visited {
    color: currentColor;
  }
`
